<template>
  <div>
    <Header
      :projectName="projectName"
      :showProductTour="false"
      :savedResults="true"
      @projectNameBtnClicked="projectNameBtnClicked"
    />
    <div class="container content">
      <div class="float-right mr-5">
        <b-button
          id="refresh-btn"
          variant="outline-primary"
          size="sm"
          @click="refreshBtnClicked"
        >
          <b-icon icon="arrow-clockwise" aria-hidden="true"></b-icon>
        </b-button>
        <b-button
          v-if="tabIndex === 0"
          v-b-tooltip.hover.html="'Download all saved results'"
          size="sm"
          :disabled="!snippets_fetched || snippets.length === 0"
          @click="downloadData(snippets, 'saved-results')"
        >
          Download All
        </b-button>
      </div>
      <div class="w-80 mx-3 mt-5">
        <div class="mb-1 tab-text">
          <b-tabs id="tab-group" v-model="tabIndex">
            <b-tab id="notes-tab" title="Saved Results"> </b-tab>
            <b-tab
              id="searches-tab"
              ref="searchesTabRef"
              title="Bookmarked Searches"
            >
            </b-tab>
          </b-tabs>
        </div>
        <b-overlay id="table-overlay" :show="tableLoading" opacity="1.0">
          <div
            v-if="
              (tabIndex === 1 && savedSearches.length > 0) ||
              (tabIndex === 0 && snippets.length > 0)
            "
          >
            <b-table
              id="results-table"
              class="results-table"
              hover
              :items="results"
              :fields="fields"
              :per-page="perPage"
              :current-page="currentPage"
            >
              <template #cell(title)="row">
                <b-button
                  variant="link"
                  @click="() => onClickProjectSearch(row.item.id)"
                  class="search-name"
                  style="text-align: start"
                >
                  <span>
                    {{ row.item.title }}
                  </span>
                </b-button>
              </template>
              <template #cell(description)="row">
                <div class="description">
                  {{ row.item.description || 'None' }}
                </div>
              </template>
              <template #cell(query)="row">
                <div>
                  {{ row.item.query || 'None' }}
                </div>
              </template>
              <template #cell(updatedAt)="row">
                <div class="actionDateEnd">
                  {{ formatDate(row.item.updatedAt) }}
                </div>
              </template>
              <template #cell(actions)="row">
                <div class="action">
                  <b-button
                    size="sm"
                    variant="primary"
                    class="mb-2 action-btn"
                    @click="editSnippetNotes(row.item)"
                  >
                    <b-icon icon="pencil-fill" aria-label="Edit"></b-icon>
                  </b-button>
                  <b-button
                    size="sm"
                    variant="primary"
                    class="mb-2 action-btn"
                    @click="deleteSnippet(row.item.id)"
                  >
                    <b-icon icon="trash-fill" aria-label="Delete"></b-icon>
                  </b-button>
                </div>
              </template>
              <template #cell(filters)="row">
                <div v-if="tabIndex === 0">
                  <b-button
                    :id="`popover-target-${row.item.id}`"
                    variant="outline-primary"
                    size="sm"
                  >
                    <b-icon icon="filter-circle" aria-label="Filter"></b-icon>
                  </b-button>
                  <b-popover
                    :target="`popover-target-${row.item.id}`"
                    triggers="hover"
                    placement="right"
                    class="popover"
                    style="
                      max-height: 300px;
                      max-width: 1400px;
                      overflow-y: auto;
                      overflow-x: hidden;
                    "
                  >
                    <template #title>Filters</template>
                    <span v-if="JSON.parse(row.item.filters).length === 0">
                      None
                    </span>
                    <b-row
                      v-for="(value, name) in formatFiltersForPopover(
                        JSON.parse(row.item.filters)
                      )"
                      :key="name"
                      class="mb-1 filters-popover"
                    >
                      <b-col sm="6" class="text-sm-left filter-field"
                        ><b>{{ reOrganizeTitle(name) }}:</b></b-col
                      >
                      <b-col sm="6" class="text-sm-left badges">
                        <span v-for="tag in value" :key="tag" class="tag"
                          ><b-icon icon="dot" aria-label="dot"></b-icon
                          >{{ tag }}</span
                        >
                      </b-col>
                    </b-row>
                  </b-popover>
                </div>
                <div v-else>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="mr-2"
                    @click="row.toggleDetails"
                  >
                    {{ row.detailsShowing ? 'Hide' : 'Show' }} Details
                  </b-button>
                </div>
              </template>
              <template #cell(snippet)="row">
                <div
                  v-if="row.item && row.item.hit_content"
                  class="result-info"
                >
                  <a
                    :id="'show-full-doc-' + row.item.id + '-' + row.index"
                    class="show-full-doc"
                    @click="
                      showFullDoc(JSON.parse(row.item.hit_content), row.index)
                    "
                  >
                    <span
                      v-if="JSON.parse(row.item.hit_content)._source.doc_title"
                    >
                      {{
                        displayList(
                          JSON.parse(row.item.hit_content)._source.doc_title,
                          40
                        )
                      }}
                    </span>
                    <span
                      v-else-if="
                        JSON.parse(row.item.hit_content)._source.doc_type
                      "
                    >
                      {{
                        displayList(
                          JSON.parse(row.item.hit_content)._source.doc_type,
                          40
                        )
                      }}
                    </span>
                    <span
                      v-else-if="
                        JSON.parse(row.item.hit_content)._source.doc_category
                      "
                    >
                      {{
                        displayList(
                          JSON.parse(row.item.hit_content)._source.doc_category,
                          40
                        )
                      }}
                    </span>
                  </a>
                  <br />
                  <div class="text-muted search-result-sub-title">
                    <span
                      v-if="
                        JSON.parse(row.item.hit_content)._source.action_date !=
                          undefined &&
                        JSON.parse(row.item.hit_content)._source.action_date
                          .length === 10
                      "
                    >
                      {{
                        [
                          JSON.parse(row.item.hit_content)._source.action_date,
                          'YYYY-MM-DD',
                        ] | moment('MMMM D, YYYY')
                      }}<span
                        v-if="
                          JSON.parse(row.item.hit_content)._source.doc_type ==
                            'Guidance Document' &&
                          JSON.parse(row.item.hit_content)._source
                            .agency_name === 'FDA'
                        "
                      >
                        (Issue Date)</span
                      ><br
                    /></span>
                    {{
                      displayList(
                        JSON.parse(row.item.hit_content)._source.doc_category,
                        40
                      )
                    }}
                    <span
                      v-if="
                        JSON.parse(row.item.hit_content)._source.doc_category !=
                          undefined &&
                        JSON.parse(row.item.hit_content)._source
                          .doc_category === 'Guidelines' &&
                        JSON.parse(row.item.hit_content)._source.doc_type !=
                          undefined &&
                        JSON.parse(row.item.hit_content)._source.doc_type
                          .length > 0
                      "
                    >
                      -
                      {{
                        displayList(
                          JSON.parse(row.item.hit_content)._source.doc_type,
                          40
                        )
                      }}</span
                    >
                    <span
                      v-if="
                        JSON.parse(row.item.hit_content)._source
                          .guidance_status !== undefined &&
                        JSON.parse(row.item.hit_content)._source.guidance_status
                          .length > 0
                      "
                    >
                      ({{
                        JSON.parse(row.item.hit_content)._source
                          .guidance_status
                      }})</span
                    >
                    <span
                      v-if="
                        JSON.parse(row.item.hit_content)._source
                          .doc_sub_category &&
                        JSON.parse(row.item.hit_content)._source
                          .doc_sub_category.length > 0
                      "
                    >
                      -
                      {{
                        JSON.parse(row.item.hit_content)._source
                          .doc_sub_category
                      }}
                    </span>
                    <span
                      v-if="
                        JSON.parse(row.item.hit_content)._source.products !=
                          undefined &&
                        JSON.parse(row.item.hit_content)._source.products
                          .length > 0
                      "
                    >
                    </span>
                    <br />
                    {{ JSON.parse(row.item.hit_content)._source.agency_name }}
                    <span
                      v-if="
                        JSON.parse(row.item.hit_content)._source
                          .agency_division_name &&
                        JSON.parse(row.item.hit_content)._source
                          .agency_division_name.length > 0
                      "
                    >
                      {{
                        Array.isArray(
                          JSON.parse(row.item.hit_content)._source
                            .agency_division_name
                        )
                          ? JSON.parse(
                              row.item.hit_content
                            )._source.agency_division_name.join(', ')
                          : JSON.parse(row.item.hit_content)._source
                              .agency_division_name
                      }}
                    </span>
                  </div>
                  <span
                    v-if="JSON.parse(row.item.hit_content)._source.products"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'pills']"
                      class="mx-2"
                      size="1x"
                      fixed-width
                    />
                    {{
                      displayList(
                        getUniqueProductNames(
                          JSON.parse(row.item.hit_content)._source.products
                        ),
                        40
                      )
                    }}
                  </span>
                </div>
                <p v-else class="text-danger">
                  Something went wrong in getting your saved results. Please log
                  out and log back in and try again. If the problem continues
                  contact us for support.
                </p>
                <b-button
                  v-if="row.item && row.item.hit_content"
                  size="sm"
                  variant="outline-primary"
                  class="mr-2 mt-2"
                  @click="row.toggleDetails"
                >
                  {{ row.detailsShowing ? 'Hide' : 'Show' }} Matching Pages
                </b-button>
              </template>
              <template #row-details="row">
                <div v-if="tabIndex === 0">
                  <b-card>
                    <b-overlay
                      id="table-overlay"
                      :show="!row.item"
                      opacity="1.0"
                    >
                      <span class="result-matching-pages">
                        {{
                          JSON.parse(row.item.hit_content).inner_hits.by_doc
                            .hits.total.value
                        }}
                        matching
                        <span
                          v-if="
                            JSON.parse(row.item.hit_content).inner_hits.by_doc
                              .hits.total.value > 1
                          "
                          >pages</span
                        ><span v-else>page</span>
                      </span>
                      <div
                        :style="
                          JSON.parse(row.item.hit_content).inner_hits.by_doc
                            .hits.total.value > 3
                            ? { height: '200px', 'overflow-y': 'scroll' }
                            : {}
                        "
                        class="result-matching-pages"
                      >
                        <div
                          v-for="inner_hit in JSON.parse(row.item.hit_content)
                            .inner_hits.by_doc.hits.hits"
                          :key="inner_hit._id"
                          class="mt-2"
                        >
                          <span
                            v-if="
                              inner_hit.highlight &&
                              inner_hit.highlight.page_content &&
                              inner_hit.highlight.page_content.length > 0
                            "
                          >
                            <i
                              ><a
                                :id="
                                  'show-full-doc-page-' +
                                  row.item._id +
                                  '-' +
                                  row.index +
                                  '-' +
                                  inner_hit._source.page_number
                                "
                                class="show-full-doc-page"
                                style="color: #007bff; cursor: pointer"
                                @click="
                                  showFullDoc(
                                    JSON.parse(row.item.hit_content),
                                    row.index,
                                    inner_hit._source.page_number
                                  )
                                "
                                >Page {{ inner_hit._source.page_number }}</a
                              ></i
                            >
                            <!-- eslint-disable -->
                            ...<span
                              v-html="
                                processHighlight(
                                  inner_hit.highlight.page_content[0]
                                )
                              "
                            ></span
                            >...
                            <!-- eslint-enable -->
                          </span>
                          <span
                            v-else-if="
                              inner_hit.highlight &&
                              inner_hit.highlight['page_content.original'] &&
                              inner_hit.highlight['page_content.original']
                                .length > 0
                            "
                          >
                            <i
                              ><a
                                :id="
                                  'show-full-doc-page-' +
                                  row.item._id +
                                  '-' +
                                  row.index +
                                  '-' +
                                  inner_hit._source.page_number
                                "
                                class="show-full-doc-page"
                                href="#"
                                @click="
                                  showFullDoc(
                                    JSON.parse(row.item.hit_content),
                                    row.index,
                                    inner_hit._source.page_number
                                  )
                                "
                                >Page {{ inner_hit._source.page_number }}</a
                              ></i
                            >
                            <!-- eslint-disable -->
                            ...<span
                              v-html="
                                inner_hit.highlight['page_content.original'][0]
                              "
                            ></span
                            >...
                            <!-- eslint-enable -->
                          </span>
                        </div>
                      </div>
                      <br />
                      <hr
                        v-if="
                          JSON.parse(row.item.hit_content)._source
                            .guidance_topics !== undefined &&
                          JSON.parse(row.item.hit_content)._source
                            .guidance_topics.length > 0
                        "
                      />
                      <div
                        v-if="
                          JSON.parse(row.item.hit_content)._source
                            .guidance_topics !== undefined &&
                          JSON.parse(row.item.hit_content)._source
                            .guidance_topics.length > 0
                        "
                        class="card-body search-result-footer"
                      >
                        <div class="search-result-footer">
                          <div
                            v-if="
                              JSON.parse(row.item.hit_content)._source
                                .guidance_topics.length > 1
                            "
                            class="float-left w-30"
                          >
                            Topics:
                            {{
                              JSON.parse(
                                row.item.hit_content
                              )._source.guidance_topics.join(', ')
                            }}
                          </div>
                          <div
                            v-else-if="
                              JSON.parse(row.item.hit_content)._source
                                .guidance_topics.length === 1
                            "
                            class="float-left w-30"
                          >
                            Topic:
                            {{
                              JSON.parse(row.item.hit_content)._source
                                .guidance_topics[0]
                            }}
                          </div>
                        </div>
                      </div>
                    </b-overlay>
                  </b-card>
                </div>
                <div v-else>
                  <b-card>
                    <b-row
                      v-if="!row.item.filters || row.item.filters.length === 0"
                    >
                      None
                    </b-row>
                    <b-row
                      v-for="(value, name) in row.item.filters"
                      v-else
                      :key="name"
                      class="mb-1"
                    >
                      <b-col sm="3" class="text-sm-left"
                        ><b> {{ reOrganizeTitle(name) }}: </b></b-col
                      >
                      <b-col class="text-sm-left">
                        <b-badge
                          v-for="tag in value"
                          :key="tag"
                          class="tag"
                          pill
                          variant="primary"
                        >
                          <span v-if="['fromDate', 'toDate'].includes(name)">
                            {{ tag.split(':')[1] }}
                          </span>
                          <span v-else>
                            {{ tag }}
                          </span>
                        </b-badge>
                      </b-col>
                    </b-row>
                  </b-card>
                </div>
              </template>
            </b-table>
            <b-pagination
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
              :limit="10"
              prev-text="Prev"
              next-text="Next"
              align="center"
              aria-controls="results-table"
            ></b-pagination>
          </div>
          <div v-else-if="!tableLoading && projectName">
            <div v-if="tabIndex === 1 && savedSearches.length <= 0">
              <b-alert class="empty-search-alert" show variant="primary"
                >You do not have any bookmarked searches for this project.
              </b-alert>
            </div>
            <div v-if="tabIndex === 0 && snippets.length === 0">
              <b-alert class="empty-search-alert" show variant="primary"
                >You do not have any saved results for this project
              </b-alert>
            </div>
          </div>
          <!-- project doesn't exist -->
          <div v-else-if="!tableLoading && !projectName">
            <b-alert class="empty-search-alert" show variant="primary"
              >Project does not exist.</b-alert
            >
          </div>
        </b-overlay>
      </div>
    </div>
    <div v-if="showPDFViewerModal">
      <PDFJSViewer
        :selectedDoc="selectedDoc"
        :selectedDocPageNum="selectedDocPageNum"
        :showButtons="false"
        @closePdfViewerModal="closePdfViewerModal"
      />
    </div>
    <ConfirmModal />
    <SnippetModal />
  </div>
</template>
<script>
import { Auth, Analytics, API, graphqlOperation } from 'aws-amplify';
import { listUserProjects } from '@/graphql/queries';
import ESService from '@/services/ESService';

import Header from '@/components/Header.vue';
import PDFJSViewer from '@/components/PDFJSViewer.vue';
import ConfirmModal from '@/components/modals/ConfirmModal';
import SnippetModal from '@/components/modals/SnippetModal';

import 'vue-loading-overlay/dist/vue-loading.css';

import { mapGetters } from 'vuex';
import { getCurUser } from '../constants/fromLocalStorageConstants';
import { getUniqueProductNames } from '@/operators/searchModifiers';

export default {
  name: 'ProjectSavedResultsPage',
  components: {
    SnippetModal,
    Header,
    PDFJSViewer,
    ConfirmModal,
  },
  props: {
    id: String,
  },
  data() {
    return {
      projectName: null,
      userinfo: null,
      pageName: null,
      searchOffset: 0, // Search result pagination offset
      currSearchTerm: 'mind',
      tableLoading: true,
      detailType: null,
      isLoading: true,
      currentPage: 1,
      perPage: 10,
      tabIndex: 0,
      savedSearches: [],
      selectedDocPageNum: 1,
      selectedDocIdx: null,
      snippets_fetched: false,
      searches_fetched: false,
      fields: [
        {
          key: 'snippet',
          label: 'Result',
        },
        {
          key: 'description',
          label: 'Notes',
        },
        { key: 'mode', label: 'Page Filter' },
        {
          key: 'query',
          label: 'Query',
        },
        {
          key: 'filters',
          label: 'Document Filters',
        },
        {
          key: 'updatedAt',
          label: 'Last Modified',
        },
        { key: 'actions', label: 'Actions' },
        { key: 'owner', label: 'Owner' },
      ],
      notes_fields: [
        {
          key: 'snippet',
          label: 'Result',
        },
        {
          key: 'description',
          label: 'Notes',
        },
        { key: 'mode', label: 'Page Filter' },
        {
          key: 'query',
          label: 'Query',
        },
        {
          key: 'filters',
          label: 'Document Filters',
        },
        {
          key: 'updatedAt',
          label: 'Last Modified',
        },
        { key: 'actions', label: 'Actions' },
      ],
      searches_fields: [
        {
          key: 'title',
          label: 'Search Name',
        },
        {
          key: 'description',
          label: 'Notes',
        },
        {
          key: 'mode',
          label: 'Page Filter',
        },
        {
          key: 'query',
          label: 'Query',
        },
        {
          key: 'filters',
          label: 'Document Filters',
        },
        {
          key: 'updatedAt',
          label: 'Last Modified',
        },
      ],
      showPDFViewerModal: false,
    };
  },
  computed: {
    ...mapGetters(['snippet', 'snippets', 'projects', 'project']),
    projects: {
      get() {
        let projects = this.$store.getters.projects;
        projects = projects.filter((project) => {
          if (project.value !== null && project.value !== '+') {
            return project;
          }
        });
        return projects;
      },
    },
    getQuotedSearchTerms() {
      return this.currSearchTerm.split('"').filter((a, i) => i % 2 === 1);
    },
    rows() {
      return this.tabIndex === 1
        ? this.savedSearches.length
        : this.snippets.length;
    },
    results() {
      return this.tabIndex === 1 ? this.savedSearches : this.snippets;
    },
  },
  watch: {
    tabIndex: function (value) {
      if (value === 1) {
        this.tableLoading = this.searches_fetched ? false : true;
        this.fields = this.searches_fields;
      } else {
        this.tableLoading = this.snippets_fetched ? false : true;
        this.fields = this.notes_fields;
      }
    },
  },
  async beforeCreate() {
    this.userinfo = await Auth.currentUserInfo();
    Analytics.record({
      name: 'visitPage',
      attributes: {
        email: this.userinfo.attributes.email,
      },
    });
  },
  async created() {
    this.getUniqueProductNames = getUniqueProductNames;

    this.tableLoading = true;
    // get project name
    const response = await API.graphql(
      graphqlOperation(listUserProjects, {
        filter: {
          userID: { eq: getCurUser().email },
          projectID: { eq: this.id },
        },
      })
    );
    const project = response.data.listUserProjects.items[0].project;
    if (!project) {
      //project doesn't exist
      this.tableLoading = false;
    } else {
      //get project name
      this.projectName = project.name;
      //get all snippets for the project
      this.$store.commit('CLEAR_SNIPPETS');

      this.projectName = project.name;
      await this.loadSavedSearches();
      this.$store
        .dispatch('FETCH_SNIPPETS', { project_id: this.id })
        .then((res) => {
          if (
            ['NO_SNIPPETS_FOR_PROJECT', 'FETCH_SNIPPETS_SUCCESS'].includes(res)
          ) {
            this.snippets_fetched = true;
            this.tableLoading = false;
          }
        });
    }
  },

  methods: {
    onClickProjectSearch(searchID) {
      this.$router.push({
        path: '/project-searchdocs-dashboard/',
        query: { projectID: this.id, searchID },
      });
    },

    sortResults(res1, res2) {
      if (res1.updatedAt < res2.updatedAt) return 1;
      else if (res1.updatedAt > res2.updatedAt) return -1;
      return 0;
    },
    projectNameBtnClicked() {
      let id;
      if (this.$route.query.projectID) {
        id = this.$route.query.projectID;
      } else {
        id = JSON.parse(localStorage.getItem('project')).id;
      }

      this.$router.push({
        path: `/project-searchdocs-dashboard/` + id,
      });
    },
    async loadSavedSearches() {
      await this.$store.dispatch('FETCH_SAVED_SEARCH', {
        project_id: this.id,
      });
      const savedSearchedArray = this.$store.state.search.savedSearch;
      const savedSearches = savedSearchedArray.map((search) =>
        this.reOrganizeSearchObject(search)
      );
      Promise.all(savedSearches).then((searches) => {
        this.savedSearches = searches;
        this.searches_fetched = true;
      });
    },
    async reOrganizeSearchObject(search) {
      const organizedSearchObject = {
        id: search.id,
        title: search.title || 'None',
        description: search.description || 'None',
        mode: search.mode,
        query: search.query || 'None',
        filters: this.reOrganizeFilters(JSON.parse(search.filters)),
        rawFilters: JSON.parse(search.filters),
        updatedAt: search.updatedAt,
        prevNumPages: search.prevNumPages || 0,
        actionDateStart: search.actionDateStart,
        actionDateEnd: search.actionDateEnd,
      };
      return organizedSearchObject;
    },

    reOrganizeFilters(filters) {
      const organizedfilter = {};
      filters.forEach((filter) => {
        if (!(filter.name in organizedfilter)) {
          organizedfilter[filter.name] = [filter.val];
        } else {
          organizedfilter[filter.name].push(filter.val);
        }
      });

      return organizedfilter;
    },
    formatFiltersForPopover(filters) {
      const organizedfilter = {};
      filters.forEach((filter) => {
        // modify filter name and val for dates
        let filter_name = '';
        let filter_val = '';
        if (filter.name === 'fromDate') {
          filter_name = 'From Date';
          filter_val = filter.val.split(':')[1];
        } else if (filter.name === 'toDate') {
          filter_name = 'To Date';
          filter_val = filter.val.split(':')[1];
        } else {
          filter_name = filter.name;
          filter_val = filter.val;
        }

        if (!(filter_name in organizedfilter)) {
          organizedfilter[filter_name] = [filter_val];
        } else {
          organizedfilter[filter_name].push(filter_val);
        }
      });
      return organizedfilter;
    },

    showDetailsBtnClicked(row) {
      this.searchSnippet(row.item).then((searchRes) => {
        row.item = searchRes;
        row.toggleDetails();
      });
    },
    async searchSnippet(item) {
      let agencyNames = '';
      let agencyDivisionNames = '';
      let docCategories = '';
      let applicationTypes = '';
      let docSubCategories = '';
      let docTypes = '';
      let guidanceTopics = '';
      let guidanceStatuses = '';
      let companyNames = '';
      let productTypes = '';
      let priorityReviews = '';
      let productClasses = '';
      let indications = '';
      let contraIndications = '';
      let brandNames = '';
      let productNames = '';
      let genericNames = '';
      let drugDesignationOrPrograms = '';
      let submissionTypes = '';
      let manufacturingSites = '';
      const filters = JSON.parse(item.filters);
      filters.map((filter) => {
        if (filter.name === 'regAgency') {
          agencyNames += agencyNames.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'regAgencyDivision') {
          agencyDivisionNames += agencyDivisionNames.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'docCategory') {
          docCategories += docCategories.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'applicationType') {
          applicationTypes += applicationTypes.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'docSubCategory') {
          docSubCategories += docSubCategories.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'submissionType') {
          submissionTypes += submissionTypes.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'docType') {
          docTypes += docTypes.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'guidanceTopic') {
          guidanceTopics += guidanceTopics.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'guidanceStatus') {
          guidanceStatuses += guidanceStatuses.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'productType') {
          productTypes += productTypes.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'priorityReview') {
          priorityReviews += priorityReviews.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'drugDesignationOrProgram') {
          drugDesignationOrPrograms += drugDesignationOrPrograms.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'indication') {
          indications += indications.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'contraIndication') {
          contraIndications += contraIndications.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'productClass') {
          productClasses += productClasses.length
            ? ';' + filter.val
            : filter.val;
        }
        if (filter.name === 'companyName') {
          companyNames += companyNames.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'genericName') {
          genericNames += genericNames.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'brandName') {
          brandNames += brandNames.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'productName') {
          productNames += productNames.length ? ';' + filter.val : filter.val;
        }
        if (filter.name === 'manufacturingSite') {
          manufacturingSites += manufacturingSites.length
            ? ';' + filter.val
            : filter.val;
        }
      });
      let search = await ESService.searchRegDocPage({
        q: item.query,
        mode: item.mode,
        agencyNames,
        agencyDivisionNames,
        docCategories,
        applicationTypes,
        docSubCategories,
        docTypes,
        guidanceTopics,
        guidanceStatuses,
        companyNames,
        genericNames,
        brandNames,
        productNames,
        productTypes,
        indications,
        contraIndications,
        productClasses,
        priorityReviews,
        drugDesignationOrPrograms,
        submissionTypes,
        manufacturingSites,
        actionDateStart: item.actionDateStartValue,
        actionDateEnd: item.actionDateEndValue,
        offset: 0,
        sortOrder: '',
      });
      search = search.data.hits.hits.filter((o1) => {
        return o1._id === item.hit_id;
      });
      if (search.length && item) {
        search[0].description = item.description;
        search[0].query = item.query;
        search[0].id = item.id;
        search[0].hit_id = item.hit_id;
        search[0].mode = item.mode;
        search[0].filters = item.filters;
        return search[0];
      }
    },
    selectProject(project_id) {
      this.project_id = project_id;
      this.$store.commit('CLEAR_SNIPPETS');
      this.tableLoading = true;
      this.$store
        .dispatch('FETCH_SNIPPETS', { project_id: project_id })
        .then(() => {
          this.tableLoading = false;
        });
    },
    // TODO move this to backend
    getDocLinkPrefix(sourceName) {
      return (
        'https://highprioritypipelinev1stack-bucket83908e77-nfink9l696lw.s3.us-east-2.amazonaws.com/' +
        sourceName +
        '/pdfs/'
      );
    },
    deleteSnippet(id) {
      this.$store.commit('SHOW_MODAL_CONFIRM_SNIPPET', {
        status: true,
        id: id,
      });
    },
    editSnippetNotes(rowItem) {
      // TODO this may not be the up to date snippet description because
      // snippets get fetched only on page refresh, and the description
      // may have been edited from the project page
      this.$store.commit('SHOW_MODAL_SNIPPET', {
        status: true,
        edit: true,
        hit: JSON.parse(rowItem.hit_content),
        filters: rowItem.filters,
        query: rowItem.query,
        mode: rowItem.mode,
        description: rowItem.description,
      });
    },
    displayList(list, maxLength = 500) {
      let listToReturn = '';
      if (!list || list.length === 0) return '';
      else if (Array.isArray(list)) {
        if (list.length === 1) listToReturn = list[0];
        else
          listToReturn =
            list.slice(0, -1).join(', ') + ' and ' + list.slice(-1)[0];
      } else listToReturn = list;
      return (
        listToReturn.slice(0, maxLength) +
        (listToReturn.length > maxLength ? '...' : '')
      );
    },
    processHighlight(content) {
      const quotedTerms = this.getQuotedSearchTerms;
      let processedContent = content;
      quotedTerms.forEach((term) => {
        const regex = new RegExp(' (' + term.trim() + ') ', 'ig');
        processedContent = processedContent.replaceAll(
          regex,
          '<mark>$1</mark>'
        );
      });
      return processedContent;
    },
    selectDoc(hit, hitIdx, docPageNum = 1) {
      this.selectedDoc = hit;
      this.selectedDocIdx = hitIdx;
      this.selectedDocPageNum = docPageNum;
    },
    getSelectedItemsInFilters(filters, field) {
      if (
        field in filters &&
        'selected' in filters[field] &&
        filters[field].selected
      ) {
        return filters[field].selected.join(', ');
      } else {
        return '';
      }
    },
    showFullDoc(hit, hitIdx, docPageNum = 1) {
      this.selectDoc(hit, hitIdx, docPageNum);
      this.showPDFViewerModal = true;
    },
    reOrganizeTitle(key) {
      switch (key) {
        case 'productName':
          return 'Product Name';
        case 'genericName':
          return 'Active Ingredient';
        case 'brandName':
          return 'Brand Name';
        case 'productClass':
          return 'Product Class';
        case 'companyName':
          return 'Company Name';
        case 'productSubset':
          return 'Product Subset';
        case 'endpoint':
          return 'Endpoint';
        case 'manufacturingSite':
          return 'Manufacturing Site';
        case 'productType':
          return 'Product Type';
        case 'biomarker':
          return 'Biomarker';
        case 'therapeuticArea':
          return 'Therapeutic Area';
        case 'indication':
          return 'Indication';
        case 'contraIndication':
          return 'Contraindication';
        case 'biomarkerCategory':
          return 'Biomarker Category';
        case 'drugDesignationOrProgram':
          return 'Drug Designation / Program';
        case 'applicationType':
          return 'Application Type';
        case 'docSubCategory':
          return 'Approval Type';
        case 'submissionType':
          return 'Submission Type';
        case 'docCategory':
          return 'Document Category';
        case 'docType':
          return 'Document Type';
        case 'guidanceTopic':
          return 'Guidance Topic';
        case 'guidanceStatus':
          return 'Guidance Status';
        case 'regAgency':
          return 'Agency';
        case 'regAgencyDivision':
          return 'Agency Division/Office';
        case 'timeline':
          return 'Timeline';
        case 'fromDate':
          return 'From Date';
        case 'toDate':
          return 'To Date';
        default:
          return key;
      }
    },
    toggleRowDetails(row, data) {
      this.detailsMask = data.split(', ');
      row.toggleDetails();
    },
    exportSavedResultsToCSV(data, filenamePrefix) {
      let csvContent = 'data:text/csv;charset=utf-8,';
      const headers = [
        'Document Title',
        'Date',
        'Document Category',
        'Approval Type',
        'Document Type',
        'Agency',
        'Agency Division Name',
        'Products',
        'Query',
        'Page Filter',
        'Document Filters',
        'Description',
      ];

      const content = [];
      data.forEach((snippet) => {
        const source = snippet._source;

        let products = '';
        source.products.forEach(
          (product) => (products += product.product_name + ';')
        );

        let title = '';
        if (source.doc_title) {
          title = source.doc_title;
        } else if (source.doc_type) {
          title =
            source.doc_type instanceof Array
              ? source.doc_type.join(';')
              : source.doc_type;
        } else {
          title = source.doc_category;
        }

        let filters = this.reOrganizeFilters(JSON.parse(snippet.filters));
        filters = filters.replaceAll(',', ';');

        const row = [
          title,
          source.action_date,
          source.doc_category,
          source.doc_sub_category,
          source.doc_type instanceof Array
            ? source.doc_type.join(';')
            : source.doc_type,
          source.agency_name,
          source.agency_division_name,
          products,
          snippet.query,
          snippet.mode,
          filters,
          snippet.description,
        ];
        content.push(row);
      });

      const metadata = [
        ['"Date/Time"', '"' + new Date() + '"'],
        ['', ''],
        ['', ''],
      ];

      csvContent += [...metadata, headers, ...content]
        .join('\n')
        .replace(/(^\[)|(\]$}{')/gm, '');

      const csvData = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', csvData);
      link.setAttribute('download', filenamePrefix + '.csv');
      link.click();
    },
    exportSavedResultsToCSV_reformat(data, filenamePrefix) {
      let csvContent = 'data:text/csv;charset=utf-8,';
      const header = [
        'Document Title',
        'Date',
        'Document Category',
        'Approval Type',
        'Document Type',
        'Agency',
        'Agency Division Name',
        'Products',
        'Query',
        'Page Filter',
        'Document Filters',
        'Description',
        'Page Number',
        'Page Content',
        'Link',
      ];

      const content = [];
      content.push(header);
      data.forEach((snippet) => {
        let filters = this.reOrganizeFilters(JSON.parse(snippet.filters));
        let formattedFilters = '';
        Object.keys(filters).forEach((key) => {
          filters[key].forEach((value) => {
            formattedFilters += value + ', ';
          });
        });
        filters = formattedFilters.substring(0, formattedFilters.length - 2);
        filters = filters.replaceAll(',', ';');

        const matching_pages = JSON.parse(snippet.hit_content).inner_hits.by_doc
          .hits.hits;
        matching_pages.forEach((match) => {
          const inner_source = match._source;

          let products = '';
          if (inner_source.products) {
            inner_source.products.forEach(
              (product) => (products += product.product_name + ';')
            );
          }

          let title = '';
          if (inner_source.doc_title) {
            title = inner_source.doc_title;
          } else if (inner_source.doc_type) {
            title = inner_source.doc_type;
          } else {
            title = inner_source.doc_category;
          }

          let page_content = '';
          if (
            match.highlight &&
            match.highlight.page_content &&
            match.highlight.page_content.length > 0
          ) {
            page_content = this.processHighlight(
              match.highlight.page_content[0]
            );
          } else if (
            match.highlight &&
            match.highlight['page_content.original'] &&
            match.highlight['page_content.original'].length > 0
          ) {
            page_content = match.highlight['page_content.original'][0];
          } else {
            page_content = inner_source.page_content.slice(
              inner_source.page_content.indexOf(' ', 100),
              inner_source.page_content.indexOf(' ', 300)
            );
          }
          page_content = page_content.replaceAll('<mark>', '');
          page_content = page_content.replaceAll('</mark>', '');
          page_content = page_content.replaceAll(/#/g, '');
          page_content = page_content.replaceAll(/[[/\]*+?\\^$|\\']/g, '\\$&');
          page_content = page_content.replace(/\n/g, ' ');

          const link = encodeURI(
            this.getDocLinkPrefix(inner_source.source_name) +
              inner_source.bts_file_name
          );

          const row = [
            title,
            inner_source.action_date,
            inner_source.doc_category,
            inner_source.doc_sub_category,
            inner_source.doc_type,
            inner_source.agency_name,
            inner_source.agency_division_name,
            products,
            snippet.query,
            snippet.mode,
            filters,
            snippet.description,
            inner_source.page_number,
            page_content,
            link,
          ];
          content.push(row.map((r) => '"' + r + '"'));
        });
      });

      const metadata = [
        ['"Date/Time"', '"' + new Date() + '"'],
        ['', ''],
        ['', ''],
      ];

      csvContent += [...metadata, ...content]
        .join('\n')
        .replace(/(^\[)|(\]$}{')/gm, '');

      const csvData = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', csvData);
      link.setAttribute('download', filenamePrefix + '.csv');
      link.click();
    },
    downloadData(data, idSuffix = '') {
      if (idSuffix === 'saved-results') {
        this.exportSavedResultsToCSV_reformat(
          data,
          'RegForesight ' + this.projectName + ' Saved Results'
        );
      }
    },
    formatDate(dateString) {
      return new Date(Date.parse(dateString)).toLocaleTimeString([], {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    refreshBtnClicked() {
      this.tableLoading = true;
      this.loadSavedSearches();
      this.$store.commit('CLEAR_SNIPPETS');

      this.$store
        .dispatch('FETCH_SNIPPETS', { project_id: this.id })
        .then((res) => {
          if (
            ['NO_SNIPPETS_FOR_PROJECT', 'FETCH_SNIPPETS_SUCCESS'].includes(res)
          ) {
            this.snippets_fetched = true;
            this.tableLoading = false;
          }
        });
    },
    closePdfViewerModal() {
      this.showPDFViewerModal = false;
    },
  },
};
</script>
<style>
/* .saved-notes-project-info {
		display: flex;
		justify-content: space-between;
		margin-top: 200px;
		font-size: 150%;
		font-weight: 700;
		width: 80%;
		margin-left: 10%;
	} */
#project-name {
  font-size: 90%;
  font-weight: 500;
  text-decoration: none;
}
.remove,
.saveSnippet {
  cursor: pointer;
}

/* .project-info {
  text-align: center;
  margin-top: 200px;
  font-size: 150%;
  font-weight: 700;
} */
/* .results-table {
		margin-left: 10%;
		width: 80%;
	}
	.results-table th {
		border: 0px;
	} */
.action {
  display: flex;
  justify-content: space-between;
  width: 90px;
}
.action-btn {
  width: 35px;
  text-align: center;
}

.show-full-doc > span {
  color: #007bff;
  cursor: pointer;
}

.show-full-doc-page > a {
  color: #2f7df6;
  cursor: pointer;
}
.popover {
  max-height: 300px;
  max-width: 1400px;
  overflow-y: auto;
  overflow-x: hidden;
  /* white-space: pre-wrap; */
}
/* .popover > .filter-field {
		width: 60%;
	} */
.badges {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}
.filters-popover {
  display: flex;
  justify-content: flex-start;
}
.empty-search-alert {
  margin-top: 50px;
  width: 40%;
  margin-left: 30%;
  text-align: center;
}
.filters {
  max-width: 200px;
  word-wrap: break-word;
}

.description {
  max-width: 200px;
  word-wrap: break-word;
}
#refresh-btn {
  margin-right: 1em;
}
.result-info,
.result-matching-pages {
  font-size: 0.9rem;
}
</style>
